import { useState } from 'react';

import { Button } from '@components/button';
import { hideModal, Modal, useModal } from '@components/modal';
import { Stack } from '@components/stack';
import { TagSelectorMulti } from '@components/tag-selector';
import { TextArea } from '@components/textarea';
import { showSuccess } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';

type DislikeFeedbackModalProps = {
  onSubmit: (tags: string[], details: string) => void;
};

export const useDislikeFeedbackModal = () => useModal(DislikeFeedbackModal);

const DislikeFeedbackModal = ({ onSubmit }: DislikeFeedbackModalProps) => {
  const { t } = useScopedTranslate(locale);

  const feedbackTags = [
    t('tagIncorrect'),
    t('tagNotAsked'),
    t('tagSlow'),
    t('tagStyle'),
    t('tagHardToUse'),
    t('tagOther'),
  ];

  const [tags, setTags] = useState<string[]>([]);
  const [details, setDetails] = useState('');

  const hasContent = tags.length > 0 || details.trim().length > 0;

  const handleSubmit = () => {
    onSubmit(tags, details);
    hideModal();
    showSuccess(t('successMessage'));
  };

  return (
    <Modal>
      <Modal.SimpleHeader title={t('title')} />
      <Modal.Content>
        <Stack space={6}>
          <TagSelectorMulti options={feedbackTags} onChange={setTags} />
          <TextArea placeholder={t('detailsPlaceholder')} onChange={setDetails} rows={3} />
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <Stack orientation="horizontal" space={4} justify="flex-end">
          <Button
            variant="primary-blue"
            onClick={handleSubmit}
            text={t('submit')}
            isDisabled={!hasContent}
          />
        </Stack>
      </Modal.Footer>
    </Modal>
  );
};

const locale = {
  en: {
    title: 'Share feedback',
    tagIncorrect: 'Incorrect or incomplete',
    tagNotAsked: 'Not what I asked for',
    tagSlow: 'Slow or buggy',
    tagStyle: 'Style or tone',
    tagHardToUse: 'Hard to use',
    tagOther: 'Other',
    detailsPlaceholder: 'Share details (optional)',
    submit: 'Submit',
    successMessage: 'Thank you for your feedback',
  },
};
